import type { LanguageCode } from "./languages";

// Best-effort "visitor's country -> default UI language" table, covering
// every ISO 3166-1 alpha-2 code present in the `countries` table (see
// supabase/migrations/20260101000002_countries.sql) - used only to turn a
// geo-IP guess (functions/api/geo.ts) into an initial language pick on a
// visitor's very first visit (see LanguageContext's resolveInitialLanguage).
//
// Deliberately a simplification, not a linguistic authority: many countries
// are multilingual (Switzerland, Belgium, India, ...) and this picks ONE
// reasonable default language per country - usually the official/majority
// language when it's one of our 40 supported languages, otherwise the most
// widely understood supported second language for that region, otherwise
// falls through to English. A visitor can always override it themselves via
// the language switcher, and that manual choice is what then gets
// remembered from then on (see LanguageContext) - this table only ever
// affects the very first, undetected visit.
export const COUNTRY_TO_LANGUAGE: Record<string, LanguageCode> = {
  AD: "es",
  AE: "ar",
  AF: "fa",
  AG: "en",
  AL: "en",
  AM: "ru",
  AO: "pt",
  AR: "es",
  AT: "de",
  AU: "en",
  AZ: "tr",
  BA: "hr",
  BB: "en",
  BD: "bn",
  BE: "nl",
  BF: "fr",
  BG: "bg",
  BH: "ar",
  BI: "fr",
  BJ: "fr",
  BN: "ms",
  BO: "es",
  BR: "pt",
  BS: "en",
  BT: "en",
  BW: "en",
  BY: "ru",
  BZ: "en",
  CA: "en",
  CD: "fr",
  CF: "fr",
  CG: "fr",
  CH: "de",
  CI: "fr",
  CL: "es",
  CM: "fr",
  CN: "zh",
  CO: "es",
  CR: "es",
  CU: "es",
  CV: "pt",
  CY: "el",
  CZ: "cs",
  DE: "de",
  DJ: "fr",
  DK: "da",
  DM: "en",
  DO: "es",
  DZ: "ar",
  EC: "es",
  EE: "en",
  EG: "ar",
  ER: "en",
  ES: "es",
  ET: "en",
  FI: "fi",
  FJ: "en",
  FM: "en",
  FR: "fr",
  GA: "fr",
  GB: "en",
  GD: "en",
  GE: "en",
  GH: "en",
  GM: "en",
  GN: "fr",
  GQ: "es",
  GR: "el",
  GT: "es",
  GW: "pt",
  GY: "en",
  HN: "es",
  HR: "hr",
  HT: "fr",
  HU: "hu",
  ID: "id",
  IE: "en",
  IL: "he",
  IN: "hi",
  IQ: "ar",
  IR: "fa",
  IS: "en",
  IT: "it",
  JM: "en",
  JO: "ar",
  JP: "ja",
  KE: "sw",
  KG: "ru",
  KH: "en",
  KI: "en",
  KM: "fr",
  KN: "en",
  KP: "ko",
  KR: "ko",
  KW: "ar",
  KZ: "ru",
  LA: "en",
  LB: "ar",
  LC: "en",
  LI: "de",
  LK: "ta",
  LR: "en",
  LS: "en",
  LT: "en",
  LU: "fr",
  LV: "en",
  LY: "ar",
  MA: "ar",
  MC: "fr",
  MD: "ro",
  ME: "sr",
  MG: "fr",
  MH: "en",
  MK: "en",
  ML: "fr",
  MM: "en",
  MN: "en",
  MR: "ar",
  MT: "en",
  MU: "fr",
  MV: "en",
  MW: "en",
  MX: "es",
  MY: "ms",
  MZ: "pt",
  NA: "en",
  NE: "fr",
  NG: "en",
  NI: "es",
  NL: "nl",
  NO: "no",
  NP: "en",
  NR: "en",
  NZ: "en",
  OM: "ar",
  PA: "es",
  PE: "es",
  PG: "en",
  PH: "tl",
  PK: "ur",
  PL: "pl",
  PS: "ar",
  PT: "pt",
  PW: "en",
  PY: "es",
  QA: "ar",
  RO: "ro",
  RS: "sr",
  RU: "ru",
  RW: "fr",
  SA: "ar",
  SB: "en",
  SC: "fr",
  SD: "ar",
  SE: "sv",
  SG: "en",
  SI: "sl",
  SK: "sk",
  SL: "en",
  SM: "it",
  SN: "fr",
  SO: "ar",
  SR: "nl",
  SS: "en",
  ST: "pt",
  SV: "es",
  SY: "ar",
  SZ: "en",
  TD: "fr",
  TG: "fr",
  TH: "th",
  TJ: "ru",
  TL: "pt",
  TM: "ru",
  TN: "ar",
  TO: "en",
  TR: "tr",
  TT: "en",
  TV: "en",
  TW: "zh",
  TZ: "sw",
  UA: "uk",
  UG: "en",
  US: "en",
  UY: "es",
  UZ: "ru",
  VA: "it",
  VC: "en",
  VE: "es",
  VN: "vi",
  VU: "en",
  WS: "en",
  YE: "ar",
  ZA: "en",
  ZM: "en",
  ZW: "en",
};
